/**
 * Learning comfort: small, invisible-to-the-child adjustments a parent can switch on.
 *
 * Design rules (see the product doc "Learning Comfort & Weekly Parent Report"):
 *  - No condition names anywhere in the product. Presets describe what they DO, not who they are for.
 *  - Change how a child gets there, never where: same topics, same mastery bar, same stars.
 *  - Off by default. A child whose parent changes nothing sees exactly the Numi everyone else sees.
 *
 * Two switches already lived on the child before this module: `autoRead` (read questions aloud) and `calm`
 * (no timer in Bubble Pop). They stay where they are; presets set them through `presetPatch`.
 */

export type Preset = "reading" | "focus" | "calm";
/** Switches stored under settings.comfort. All default to off. */
export interface Comfort {
  presets?: Preset[];
  /** Larger text and more space between words and lines. */
  bigText?: boolean;
  /** Tap one of a few answers instead of typing a number. */
  choices?: boolean;
  /** Rounds of 3 questions instead of 5. */
  shortRounds?: boolean;
  /** A 30-second stretch break with Numi after about 9 minutes of play. */
  breaks?: boolean;
  /** A gentle "take your time" from Numi after 30 seconds without a tap. */
  nudgeIdle?: boolean;
  /** No confetti, no bouncing, no shaking. */
  lessMotion?: boolean;
  /** Quieter sound effects. */
  softSound?: boolean;
  /** Numi's AI replies use short, literal sentences (no idioms or jokes). */
  literal?: boolean;
  /** Tell the child what is coming before a round starts, and say clearly when it ends. */
  preview?: boolean;
}
export type ComfortKey = Exclude<keyof Comfort, "presets">;
/** The two older switches that live directly on the child's settings. */
export type LegacyKey = "autoRead" | "calm";
export type SwitchKey = ComfortKey | LegacyKey;

export const COMFORT_KEYS: ComfortKey[] = ["bigText", "choices", "shortRounds", "breaks", "nudgeIdle", "lessMotion", "softSound", "literal", "preview"];
export const PRESET_IDS: Preset[] = ["reading", "focus", "calm"];

export const PRESETS: Record<Preset, { title: string; blurb: string; sets: SwitchKey[] }> = {
  reading: { title: "Reading helper", blurb: "Reads every question aloud, bigger text, tap-to-pick answers, no timers.",
    sets: ["autoRead", "bigText", "choices", "calm"] },
  focus: { title: "Focus helper", blurb: "Shorter rounds, a stretch break every 9 minutes, a gentle nudge when attention drifts.",
    sets: ["shortRounds", "breaks", "nudgeIdle"] },
  calm: { title: "Calm & predictable", blurb: "Says what's coming first, no confetti or shaking, softer sounds, plain literal words.",
    sets: ["preview", "lessMotion", "softSound", "literal", "calm"] },
};

/** Behaviour questions for the parent. Never a diagnosis question. A "yes" suggests the matching preset. */
export const COMFORT_QUESTIONS: { preset: Preset; q: string }[] = [
  { preset: "reading", q: "Does reading long questions slow your child down?" },
  { preset: "focus", q: "Does your child's attention drift after a few minutes?" },
  { preset: "calm", q: "Do surprises, loud sounds or sudden changes upset your child?" },
];

/** Every switch a parent can fine-tune, in the order shown. */
export const SWITCHES: { key: SwitchKey; label: string }[] = [
  { key: "autoRead", label: "Read questions aloud automatically" },
  { key: "bigText", label: "Bigger text and more space" },
  { key: "choices", label: "Tap to pick answers instead of typing" },
  { key: "calm", label: "No timers (Bubble Pop at your own speed)" },
  { key: "shortRounds", label: "Shorter rounds: 3 questions instead of 5" },
  { key: "breaks", label: "A 30-second stretch break every 9 minutes" },
  { key: "nudgeIdle", label: "A gentle nudge after 30 seconds without a tap" },
  { key: "preview", label: "Say what's coming before each round" },
  { key: "lessMotion", label: "Less motion: no confetti, bouncing or shaking" },
  { key: "softSound", label: "Softer sounds" },
  { key: "literal", label: "Plain, literal words from Numi (no idioms or jokes)" },
];

/** Keeps only known keys with boolean values (server-side, so nothing else can be stored). */
export function cleanComfort(x: unknown): Comfort {
  const out: Comfort = {}; const o = (x && typeof x === "object" ? x : {}) as Record<string, unknown>;
  const ps = Array.isArray(o.presets) ? o.presets.filter((p): p is Preset => PRESET_IDS.includes(p as Preset)) : [];
  if (ps.length) out.presets = Array.from(new Set(ps));
  for (const k of COMFORT_KEYS) if (o[k] === true) out[k] = true;
  return out;
}

interface SettingsLike { autoRead?: boolean | null; calm?: boolean | null; comfort?: Comfort }

/**
 * The settings change for turning a preset on or off. Turning one off clears only the switches no other
 * active preset still needs, so presets combine cleanly. Returns the new comfort object plus the two legacy switches.
 */
export function presetPatch(s: SettingsLike, preset: Preset, on: boolean): { comfort: Comfort; autoRead?: boolean | null; calm?: boolean | null } {
  const cur = cleanComfort(s.comfort);
  const presets = new Set(cur.presets || []); on ? presets.add(preset) : presets.delete(preset);
  const next: Comfort = { ...cur, presets: [...presets] };
  const out: { comfort: Comfort; autoRead?: boolean | null; calm?: boolean | null } = { comfort: next, autoRead: s.autoRead ?? null, calm: s.calm ?? null };
  const stillNeeded = (k: SwitchKey) => [...presets].some((p) => PRESETS[p].sets.includes(k));
  for (const k of PRESETS[preset].sets) {
    const v = on ? true : stillNeeded(k);
    if (k === "autoRead" || k === "calm") out[k] = on ? true : stillNeeded(k) ? true : null; // null = back to the class default
    else if (v) next[k] = true; else delete next[k];
  }
  if (!next.presets!.length) delete next.presets;
  return out;
}

/** Suggested presets from the parent's yes/no answers. */
export const suggestPresets = (yes: Partial<Record<Preset, boolean>>): Preset[] => PRESET_IDS.filter((p) => yes[p]);

/* ---------- Pacing: notice when a child is struggling, and respond gently ---------- */

/** One finished question. */
export interface PaceSignal {
  /** Right on the first try. */
  firstTry: boolean;
  /** Wrong tries before finishing (3 = Numi showed the answer). */
  tries: number;
  /** Times the Hint button was pressed. */
  hints: number;
  /** Time from the question appearing to it being finished. */
  ms: number;
}
export type PaceResponse = "easier" | "picture" | "break";
export interface PaceState { hist: PaceSignal[]; struggles: number; sinceLast: number; quits: number }
export const newPace = (): PaceState => ({ hist: [], struggles: 0, sinceLast: 99, quits: 0 });

/** At least this many questions between two responses, so Numi never nags. */
export const PACE_COOLDOWN = 3;

/** Why a run of questions looks like a struggle, or null if it doesn't. */
export function struggleReason(hist: PaceSignal[], quits = 0): string | null {
  const n = hist.length; if (!n) return null;
  let miss = 0; for (let i = n - 1; i >= 0 && !hist[i].firstTry; i--) miss++;
  if (miss >= 3) return "three misses in a row";
  if (miss >= 2 && quits > 0) return "misses after leaving a round";
  if (n >= 2 && hist[n - 1].hints >= 2 && hist[n - 2].hints >= 2 && !hist[n - 1].firstTry) return "needing several hints in a row";
  const last = hist[n - 1];
  const okTimes = hist.slice(-7, -1).filter((h) => h.firstTry).map((h) => h.ms).sort((a, b) => a - b);
  if (!last.firstTry && last.tries >= 2 && okTimes.length >= 3) {
    const median = okTimes[Math.floor(okTimes.length / 2)];
    if (last.ms > 45e3 && last.ms > 3 * median) return "a long, hard question";
  }
  return null;
}

/**
 * Records a finished question and decides whether Numi should step in. Responses climb gently through the session:
 * first an easier question, then the picture lesson, then a break. Mutates and returns `st`.
 */
export function paceAfter(st: PaceState, sig: PaceSignal): PaceResponse | null {
  st.hist = [...st.hist, sig].slice(-12); st.sinceLast++;
  if (st.sinceLast < PACE_COOLDOWN) return null;
  if (!struggleReason(st.hist, st.quits)) return null;
  st.struggles++; st.sinceLast = 0; st.quits = 0;
  st.hist = []; // start the window fresh so the same misses never count twice
  return st.struggles === 1 ? "easier" : st.struggles === 2 ? "picture" : "break";
}
